import React, { useRef } from 'react';

import { cardMap } from './card-map';
import { useSearchAndShuffleContext } from './context';
import { Container, EmptyState, GlobalStyles } from './deck-card.styled';
import type { CardData } from './types';
import { useActions } from './use-actions';
import { useSwipeableCard } from './use-swipeable-card';

type DeckCardProps = {
  deckIndex: number;
  cardIndex: number;
};

const DeckCardEmptyState: React.FC<{ data: CardData['data'] }> = () => (
  <EmptyState />
);

export const DeckCard: React.FC<DeckCardProps> = ({ deckIndex, cardIndex }) => {
  const allActions = useActions();
  const { decks, actionMap, isShuffling, cardLinksEnabled, config } =
    useSearchAndShuffleContext();
  const cardRef = useRef<HTMLDivElement>(null);

  const card = decks[deckIndex]?.cards?.[cardIndex];
  const action =
    card?.data?.id && actionMap?.[card.data.id]
      ? actionMap[card.data.id]
      : undefined;

  const actions = allActions?.[card?.type];

  const CardComponent =
    cardMap[card?.type] ?? config?.EmptyStateComponent ?? DeckCardEmptyState;

  const {
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    swipeDirection,
    isSwiping,
    currentPos,
  } = useSwipeableCard({
    handleSwipe: actions?.handleSwipe,
    action,
    deckIndex: deckIndex,
  });

  const onPointer =
    cardIndex === 0 && !!card && card.type !== 'Error'
      ? {
          onPointerDown: handlePointerDown,
          onPointerMove: handlePointerMove,
          onPointerUp: handlePointerUp,
        }
      : {};

  const cardClickHandler = (e: React.MouseEvent<HTMLDivElement>): void => {
    const clickInCard =
      cardRef.current &&
      (e.target === cardRef.current ||
        cardRef.current.contains(e.target as Node));

    // handle card clicks that were not intercepted by preventDefault
    if (clickInCard && cardLinksEnabled.current && actions?.cardClickHandler) {
      actions.cardClickHandler(card, deckIndex);
    }
  };

  return (
    <>
      <GlobalStyles />
      <Container
        {...onPointer}
        aria-hidden={cardIndex !== 0}
        onClick={cardClickHandler}
        ref={cardRef}
        $direction={swipeDirection}
        $isSwiping={isSwiping}
        $currentPos={currentPos}
        $index={cardIndex}
        $prepareShuffling={
          isShuffling !== undefined && isShuffling <= deckIndex
        }
      >
        <CardComponent data={card?.data} />
      </Container>
    </>
  );
};
